import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';
import type { LineItem, CreateLineItem, UpdateLineItem } from '@estimator/shared';

export function useLineItems(areaId: string | undefined) {
  return useQuery({
    queryKey: ['line-items', areaId],
    queryFn: async () => {
      const { data } = await apiClient.get<LineItem[]>('/line-items', {
        params: { area_id: areaId },
      });
      return data;
    },
    enabled: !!areaId,
  });
}

export function useLineItemTemplates() {
  return useQuery({
    queryKey: ['line-items', 'templates'],
    queryFn: async () => {
      const { data } = await apiClient.get<LineItem[]>('/line-items/templates');
      return data;
    },
  });
}

export function useLineItem(id: string | null) {
  return useQuery({
    queryKey: ['line-items', 'detail', id],
    queryFn: async () => {
      const { data } = await apiClient.get<LineItem>(`/line-items/${id}`);
      return data;
    },
    enabled: !!id,
  });
}

export function useCreateLineItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateLineItem) => {
      const { data } = await apiClient.post<LineItem>('/line-items', body);
      return data;
    },
    onSuccess: (_data, vars) =>
      queryClient.invalidateQueries({ queryKey: ['line-items', vars.area_id] }),
  });
}

export function useUpdateLineItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: UpdateLineItem & { id: string }) => {
      const { data } = await apiClient.patch<LineItem>(`/line-items/${id}`, body);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['line-items'] });
      queryClient.invalidateQueries({ queryKey: ['calculations'] });
    },
  });
}

export function useDeleteLineItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/line-items/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['line-items'] });
      queryClient.invalidateQueries({ queryKey: ['calculations'] });
    },
  });
}

export function useDuplicateLineItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, overrides }: { id: string; overrides?: Partial<UpdateLineItem> }) => {
      const { data } = await apiClient.post<LineItem>(
        `/line-items/${id}/duplicate`,
        overrides ? { overrides } : undefined,
      );
      return data;
    },
    // ['line-items'] prefix-matches ['line-items','templates'], so the Cria
    // Library list refreshes without an explicit refetch.
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['line-items'] });
      queryClient.invalidateQueries({ queryKey: ['calculations'] });
    },
  });
}

export function useBulkUpdateLineItems() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ ids, updates }: { ids: string[]; updates: Partial<UpdateLineItem> }) => {
      const { data } = await apiClient.post<{ updated: number }>('/line-items/bulk-update', {
        ids,
        updates,
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['line-items'] });
      queryClient.invalidateQueries({ queryKey: ['calculations'] });
    },
  });
}

export function useAddLineItemHardware() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      lineItemId,
      hardware_id,
      quantity,
    }: {
      lineItemId: string;
      hardware_id: string;
      quantity?: number;
    }) => {
      const { data } = await apiClient.post(`/line-items/${lineItemId}/hardware`, {
        hardware_id,
        quantity,
      });
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['line-items'] }),
  });
}

export function useRemoveLineItemHardware() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ lineItemId, hwId }: { lineItemId: string; hwId: string }) => {
      await apiClient.delete(`/line-items/${lineItemId}/hardware/${hwId}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['line-items'] }),
  });
}

export function useUpdateLineItemHardware() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      lineItemId,
      hwId,
      quantity,
    }: {
      lineItemId: string;
      hwId: string;
      quantity: number;
    }) => {
      const { data } = await apiClient.patch(`/line-items/${lineItemId}/hardware/${hwId}`, {
        quantity,
      });
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['line-items'] }),
  });
}
